import React, { useEffect, useState, useMemo } from "react";
import '../../../styles/Auth/return.css';
import { useNavigate } from "react-router-dom";
import { getData, postData } from '../../../webService/webservice.js';
import { toast } from "react-toastify";
import TVFMLoader from "../Loader/loader.jsx";
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox } from "@mui/material";
import { AsyncPaginate } from "react-select-async-paginate";

const ReturnAddPage = () => {
    const navigate = useNavigate();
    const role = useMemo(() => localStorage.getItem("role"), []);
    const adminStoreIds = useMemo(() => JSON.parse(localStorage.getItem("storeIds") || "[]"), []);
    const initialStoreId = adminStoreIds[0] || "";
    const token = useMemo(() => localStorage.getItem("Token"), []);

    const [note, setNote] = useState("");
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [paymentList, setPaymentList] = useState([{ method: "", amount: "" }]);

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [returnItems, setReturnItems] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    const [loaderText, setLoaderText] = useState('');



    /* ---------------- FOR ASYNC SELECT ---------------- */
    const loadStores = async (search, loadedOptions, { page }) => {
        try {
            // SUPER ADMIN
            if (role === "SuperAdmin") {
                const res = await getData("stores/stores", {
                    page,
                    limit: 10,
                    search
                }, token);

                const results = res?.data?.results || [];
                const hasMore = res?.data?.currentPage < res?.data?.totalPages;

                return {
                    options: results.map(s => ({
                        label: s.storeName,
                        value: s._id,
                        data: s
                    })),
                    hasMore,
                    additional: { page: page + 1 }
                };
            }

            // STORE ADMIN / FRANCHISE ADMIN
            else {
                // If we already loaded, no need to paginate (local array logic)
                if (page > 1) return { options: [], hasMore: false };

                let fetchedStores = [];
                if (adminStoreIds && adminStoreIds.length > 0) {
                    const storePromises = adminStoreIds.map(id =>
                        getData(`stores/store/${id}`, {}, token)
                    );
                    const storeResults = await Promise.all(storePromises);
                    fetchedStores = storeResults.map(r => r?.data).filter(Boolean);
                }

                if (search) {
                    fetchedStores = fetchedStores.filter(s =>
                        s.storeName.toLowerCase().includes(search.toLowerCase())
                    );
                }

                return {
                    options: fetchedStores.map(s => ({
                        label: s.storeName,
                        value: s._id,
                        data: s
                    })),
                    hasMore: false,
                    additional: { page: 1 }
                };
            }

        } catch (err) {
            console.error("Load Stores Error", err);
            return { options: [], hasMore: false };
        }
    };

    const loadCustomers = async (search, loadedOptions, { page }) => {
        if (!selectedStore) return { options: [], hasMore: false };

        try {
            const res = await getData("customers/customers", {
                storeId: selectedStore._id,
                search,
                page,
                limit: 10
            }, token);

            const pageData = res?.data?.[0]; // common pattern in this project
            const results = pageData?.paginatedResults || [];
            const hasMore = pageData?.currentPage < pageData?.totalPages;

            return {
                options: results.map(c => ({
                    label: `${c.name} (${c.phoneNumber})`,
                    value: c._id,
                    data: c
                })),
                hasMore,
                additional: { page: page + 1 }
            };
        } catch (err) {
            console.error("Load Customers Error", err);
            return { options: [], hasMore: false };
        }
    };

    const loadInvoices = async (search, loadedOptions, { page }) => {
        if (!selectedCustomer) return { options: [], hasMore: false };

        try {
            // Note: Current API `invoices/invoices/customer` might not support search/pagination fully
            // Assuming it returns all or we filter locally. Using standard pattern for safety.
            const res = await getData("invoices/invoices/customer", {
                customerId: selectedCustomer._id,
                limit: 100, // Ensure we get enough recent invoices
                sort: JSON.stringify([["createdAt", -1]])
            }, token);

            let invoices = res?.data?.results || res?.data || [];

            // Local Filter for Search & Return Eligibility
            if (search) {
                invoices = invoices.filter(inv =>
                    inv.invoiceNo.toLowerCase().includes(search.toLowerCase())
                );
            }

            // Filter eligible invoices (same logic as before)
            const eligible = invoices.filter(inv => {
                const totalQty = inv.items?.reduce((s, i) => s + (i.qty || 0), 0) || 0;
                const returnedQty = inv.items?.reduce((s, i) => s + (i.returnedQty || 0), 0) || 0;
                return (totalQty - returnedQty) > 0;
            });

            // Since API might not paginate, we return all at once (page 1 only)
            if (page > 1) return { options: [], hasMore: false };

            return {
                options: eligible.map(inv => ({
                    label: inv.invoiceNo,
                    value: inv._id,
                    data: inv
                })),
                hasMore: false, // pagination not fully utilized for this endpoint yet
                additional: { page: page + 1 }
            };

        } catch (err) {
            console.error("Load Invoices Error", err);
            return { options: [], hasMore: false };
        }
    };

    /* ---------------- HANDLERS ---------------- */

    const handleStoreSelect = (option) => {
        const store = option?.data;
        setSelectedStore(store || null);

        // Reset downstream
        setSelectedCustomer(null);
        setSelectedInvoice(null);
        setInvoices([]);
        setReturnItems([]);
    };

    const handleCustomerSelect = (option) => {
        const customer = option?.data;
        setSelectedCustomer(customer || null);

        // Reset downstream
        setSelectedInvoice(null);
        setReturnItems([]);
    };

    const handleInvoiceSelect = async (option) => {
        const invoiceId = option?.value;
        if (!invoiceId) {
            setSelectedInvoice(null);
            setReturnItems([]);
            return;
        }

        // Reuse existing detailed fetch logic
        // We call the old function logic here inline or refactor
        // Let's copy the logic from old handleInvoiceSelect but adapted

        try {
            const res = await getData(`/invoices/invoice/${invoiceId}`, {}, token);
            const invoiceDetails = res?.data;
            if (!invoiceDetails) return;

            const preparedItems = (invoiceDetails.items || [])
                .filter(item => {
                    const returnedQty = item.returnedQty || 0;
                    return item.qty - returnedQty > 0;
                })
                .map(item => {
                    const returnedQty = item.returnedQty || 0;
                    const remainingQty = item.qty - returnedQty;

                    // Support both inclusive and exclusive logic
                    const itemCat = String(item.category || "").toLowerCase();

                    // Product is ALWAYS Inclusive. 
                    // Service & Membership follow the invoice-level gstType (Inclusive/Exclusive).
                    const isItemInclusive = itemCat === "product" || invoiceDetails.gstType === "Inclusive";

                    const itemTotalGST = Number(item.gstAmount || 0);
                    const itemSubTotal = Number(item.total || 0);

                    // Taxable portion: 
                    // 1. Prefer saved taxableAmount from POS
                    // 2. Fallback to calculation based on inclusive/exclusive status
                    let taxableVal = Number(item.taxableAmount || 0);
                    if (!item.taxableAmount) {
                        taxableVal = isItemInclusive ? (itemSubTotal - itemTotalGST) : itemSubTotal;
                    }

                    return {
                        _id: item._id,
                        productId: item.productId?._id,
                        serviceId: item.serviceId?._id,
                        subscriptionId: item.subscriptionId?._id,
                        name: item.productId?.title || item.serviceId?.serviceName || item.subscriptionId?.subscriptionName || "Item",
                        image: item.productId?.productImageObject?.uri || "/assets/default.png",

                        originalQty: item.qty,
                        returnedQty,
                        remainingQty,
                        returnQty: 0,

                        price: Number(item.price || 0),
                        taxableAmount: taxableVal,
                        gstAmount: itemTotalGST,
                        category: item.category,

                        isSelected: false
                    };
                });

            setReturnItems(preparedItems);
            setSelectedInvoice(invoiceDetails);
        } catch (err) {
            console.error("Error fetching invoice details", err);
            setReturnItems([]);
        }
    };
    useEffect(() => {
        if (role !== "SuperAdmin" && adminStoreIds?.length > 0) {
            const loadInitial = async () => {
                const res = await getData(`stores/store/${adminStoreIds[0]}`, {}, token);
                if (res?.data) {
                    setSelectedStore(res.data);
                }
            };
            loadInitial();
        }
    }, [role, adminStoreIds, token]);



    const increaseReturnQty = (id) => {
        setReturnItems(prev =>
            prev.map(item =>
                item._id === id && item.returnQty < item.remainingQty
                    ? { ...item, returnQty: item.returnQty + 1 }
                    : item
            )
        );
    };


    const decreaseReturnQty = (id) => {
        setReturnItems(prev =>
            prev.map(item =>
                item._id === id && item.returnQty > 0
                    ? { ...item, returnQty: item.returnQty - 1 }
                    : item
            )
        );
    };




    const addPaymentRow = () => {
        setPaymentList(prev => [...prev, { method: "Cash", amount: "" }]);
    };

    const removePaymentRow = (idx) => {
        setPaymentList(prev => prev.filter((_, i) => i !== idx));
    };


    // 🔹 Calculation Logic
    const calcReturnTaxable = (item) => {
        if (!item.returnQty) return 0;
        return (item.taxableAmount / item.originalQty) * item.returnQty;
    };

    const calcReturnGST = (item) => {
        if (!item.returnQty) return 0;
        return (item.gstAmount / item.originalQty) * item.returnQty;
    };

    const calcReturnRowTotal = (item) => {
        return calcReturnTaxable(item) + calcReturnGST(item);
    };

    // Totals for selected items
    const selectedTaxableSum = returnItems.reduce((acc, i) => acc + calcReturnTaxable(i), 0);
    const selectedGstSum = returnItems.reduce((acc, i) => acc + calcReturnGST(i), 0);
    const selectedSubTotal = selectedTaxableSum + selectedGstSum;

    // Discount & Round-off Proportion
    // Calculate accurate invSubTotal (Total PAID for all items before overall discounts)
    // For inclusive: taxable + gst. For exclusive: taxable + gst.
    // This sum correctly handles the mixed nature of Products(Inc) and Services(Inc/Exc).
    const invSubTotal = (selectedInvoice?.items || []).reduce((sum, item) => {
        const taxable = Number(item.taxableAmount || 0);
        const gst = Number(item.gstAmount || 0);
        return sum + taxable + gst;
    }, 0);

    const returnProportion = invSubTotal > 0 ? (selectedSubTotal / invSubTotal) : 0;

    const returnDiscount = Number(selectedInvoice?.discount || 0) * returnProportion;
    const returnRoundOff = Number(selectedInvoice?.roundOff || 0) * returnProportion;

    // Final Grand Total for Return
    const grandTotal = Math.round((selectedSubTotal - returnDiscount + returnRoundOff) * 100) / 100;

    const calcSGST = selectedGstSum / 2;
    const calcCGST = selectedGstSum / 2;

    const totalReturnProducts = returnItems.filter(i => i.returnQty > 0).length;
    const totalReturnItemsCount = returnItems.reduce((sum, item) => sum + item.returnQty, 0);
    const amountRemaining = Math.max(0, grandTotal - paymentList.reduce((sum, p) => sum + Number(p.amount || 0), 0));
    const formatCurrency = (num) => `₹${Number(num || 0).toFixed(2)}`;

    const toggleItemSelection = (id) => {
        setReturnItems(prev =>
            prev.map(item => {
                if (item._id !== id) return item;

                if (item.remainingQty === 0) {
                    toast.error("No quantity left to return");
                    return item;
                }

                const isSelect = !item.isSelected;

                return {
                    ...item,
                    isSelected: isSelect,
                    returnQty: isSelect ? 1 : 0
                };
            })
        );
    };


    const handleAddReturn = async () => {
        const totalPaid = paymentList.reduce((sum, p) => sum + Number(p.amount), 0);
        if (Math.abs(totalPaid - grandTotal) > 0.01) {
            return toast.error("Please settle the full payment before adding return.");
        }

        if (!selectedInvoice) {
            toast.error("Please select an invoice");
            return;
        }

        const payloadItems = returnItems
            .filter(i => i.returnQty > 0)
            .map(i => {
                const taxableUnitPrice = i.taxableAmount / i.originalQty;
                const lineTaxableTotal = taxableUnitPrice * i.returnQty;
                const lineGSTTotal = (i.gstAmount / i.originalQty) * i.returnQty;

                return {
                    productId: i.category === "product" ? i.productId : null,
                    serviceId: i.category === "service" ? i.serviceId : null,
                    subscriptionId: i.category === "membership" ? i.subscriptionId : null,

                    description: i.name,
                    price: taxableUnitPrice, // Standard POS practice: price is taxable
                    quantity: i.quantity || i.returnQty,
                    total: lineTaxableTotal,
                    gstAmount: lineGSTTotal, // Send GST per line if possible
                    returnReason: "No reason"
                };
            });

        if (!payloadItems.length)
            return toast.error("Select at least one item");

        const payload = {
            invoiceId: selectedInvoice._id,
            customerId: selectedCustomer._id,
            storeId: selectedStore._id,
            returnItems: payloadItems,
            note: note || "",
            totalAmount: selectedTaxableSum,
            sgst: calcSGST,
            cgst: calcCGST,
            gstRate: selectedInvoice?.gstRate || 0,
            discount: returnDiscount,
            grandTotal,
            paymentMode: paymentList
        };

        setShowLoader(true);

        try {
            const res = await postData("returns/", payload, token);

            if (res.status === 200 || res.status === 201) {
                toast.success("Return created successfully");
                printReturn(res.data);

                setReturnItems([]);
                setSelectedInvoice(null);
                setSelectedCustomer(null);
                setSelectedStore(null);
                setNote("");
                setPaymentList([{ method: "Cash", amount: "" }]);
                navigate("/return");
            } else {
                toast.error(res.message || "Error creating return");
            }
        } catch (err) {
            console.error("Error creating return:", err);
            toast.error("Error creating return");
        } finally {
            setTimeout(() => setShowLoader(false), 2000);
        }
    };


    const printReturn = (returnData) => {
        const formatCurrency = (num) => `₹${Number(num ?? 0).toFixed(2)}`;

        // ---- FIXED HEADER VALUES ----
        const invoiceNo = returnData.returnInvoiceNo || "-";
        const customerName = returnData.customerId?.name || "Walk-in";
        const storeName = returnData.storeId?.storeName || "-";


        // ---- FIXED ITEMS HTML ----
        const itemsHtml = returnData.items.map(item => {
            // Find the corresponding item in the invoice to get the title/name
            const invoiceItem = selectedInvoice?.items?.find(invItem =>
                (invItem.productId?._id === (item.productId?._id || item.productId)) ||
                (invItem.serviceId?._id === (item.serviceId?._id || item.serviceId)) ||
                (invItem.subscriptionId?._id === (item.subscriptionId?._id || item.subscriptionId))
            );

            const itemName =
                invoiceItem?.productId?.title ||
                invoiceItem?.serviceId?.serviceName ||
                invoiceItem?.subscriptionId?.subscriptionName ||
                item.description ||
                "-";

            const lineTotal = (item.price - (item.perItemDiscount ?? 0)) * item.quantity;

            return `
            <tr>
                <td>${itemName}</td>
                <td>${item.quantity}</td>
                <td>${formatCurrency(item.price)}</td>
                <td class="right">${formatCurrency(lineTotal)}</td>
            </tr>
        `;
        }).join("");

        const totalAmount = returnData.totalAmount ?? 0;
        const totalItemDiscount = returnData.items.reduce(
            (sum, i) => sum + (i.perItemDiscount ?? 0) * i.quantity,
            0
        );
        const overallDiscount = returnData.discount ?? 0;
        const calcSGST = returnData.sgst ?? 0;
        const calcCGST = returnData.cgst ?? 0;
        const grandTotal = returnData.grandTotal ?? 0;

        const paymentModesHtml = (returnData.paymentMode || []).map(p => `
            <tr>
                <td>${p.method}</td>
                <td class="right">${formatCurrency(p.amount)}</td>
            </tr>
        `).join("");

        const totalGST = calcSGST + calcCGST;
        const taxableAmount = Math.max(0, grandTotal - totalGST + overallDiscount);

        const receiptHtml = `
<html>
<head>
    <title>Return Receipt</title>
    <style>
        body {
            font-family: 'Courier New', monospace;
            font-size: 11px;
            width: 72mm; /* Standard thermal width */
            margin: 0;
            padding: 10px;
        }
        .center { text-align: center; }
        .separator { border-bottom: 1px dashed #000; margin: 8px 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: 2px 0; }
        .right { text-align: right; }
        .bold { font-weight: bold; }
        .fs-large { font-size: 14px; }
    </style>
</head>
<body>

    <div class="center">
        <h3 style="margin: 0;">${storeName}</h3>
        <p style="margin: 5px 0; font-weight: bold;">RETURN RECEIPT</p>
        <div class="separator"></div>
    </div>

    <table style="margin-bottom: 5px;">
        <tr><td><strong>Return No:</strong></td><td class="right">${invoiceNo}</td></tr>
        <tr><td><strong>Ref Inv:</strong></td><td class="right">${selectedInvoice?.invoiceNo || "-"}</td></tr>
        <tr><td><strong>Customer:</strong></td><td class="right">${customerName}</td></tr>
        <tr><td><strong>Date:</strong></td><td class="right">${new Date().toLocaleDateString('en-GB')}, ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</td></tr>
    </table>

    <div class="separator"></div>

    <table>
        <thead>
            <tr style="border-bottom: 1px solid #000;">
                <th style="text-align:left;">Item</th>
                <th>Qty</th>
                <th class="right">Taxable</th>
                <th class="right">GST</th>
                <th class="right">Total</th>
            </tr>
        </thead>
        <tbody>
            ${returnData.items.map(item => {
            const invoiceItem = selectedInvoice?.items?.find(invItem =>
                (invItem.productId?._id === (item.productId?._id || item.productId)) ||
                (invItem.serviceId?._id === (item.serviceId?._id || item.serviceId))
            );
            const itemName = invoiceItem?.productId?.title || invoiceItem?.serviceId?.serviceName || item.description || "-";

            // Proportional GST for this item
            const itemTotal = Number(item.total || 0);
            const itemGst = Number(item.gstAmount || 0);

            return `
                    <tr>
                        <td style="max-width: 80px;">${itemName}</td>
                        <td style="text-align: center;">${item.quantity}</td>
                        <td class="right">${formatCurrency(itemTotal)}</td>
                        <td class="right">${formatCurrency(itemGst)}</td>
                        <td class="right">${formatCurrency(itemTotal + itemGst)}</td>
                    </tr>
                `;
        }).join("")}
        </tbody>
    </table>

    <div class="separator"></div>

    <table>
        <tr><td>Amount (Before Tax)</td><td class="right">${formatCurrency(taxableAmount)}</td></tr>
        ${totalGST > 0 ? `
            <tr><td>Total GST</td><td class="right">${formatCurrency(totalGST)}</td></tr>
            <tr style="font-size: 10px; color: #555;">
                <td style="padding-left: 10px;">- CGST</td><td class="right">${formatCurrency(calcCGST)}</td>
            </tr>
            <tr style="font-size: 10px; color: #555;">
                <td style="padding-left: 10px;">- SGST</td><td class="right">${formatCurrency(calcSGST)}</td>
            </tr>
        ` : ""}
        ${overallDiscount > 0 ? `<tr><td>Overall Discount</td><td class="right">- ${formatCurrency(overallDiscount)}</td></tr>` : ""}
        <tr class="bold fs-large">
            <td>Grand Total</td>
            <td class="right">${formatCurrency(grandTotal)}</td>
        </tr>
    </table>

    <div class="separator"></div>

    <table style="margin-top: 5px;">
        <tr><td colspan="2" class="bold">Refund Details:</td></tr>
        ${paymentModesHtml || "<tr><td>-</td><td class='right'>₹0.00</td></tr>"}
    </table>

    <div class="separator"></div>
    <div class="center"><p style="margin-top: 10px;">*** Refund Processed Successfully ***</p></div>

</body>
</html>
`;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(receiptHtml);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };


    return (
        <div className="return-container">
            {
                showLoader && (
                    <div className="scissor-overlay">
                        <div className="scissor-loader">
                            <TVFMLoader />
                        </div>
                    </div>
                )
            }
            <Typography variant="h6" sx={{ mb: 1 }}>
                Add Return
            </Typography>
            <div className="return-top-inputs">
                <div className="return-input-group">
                    <label>Branch Name</label>
                    <AsyncPaginate
                        value={selectedStore ? {
                            label: selectedStore.storeName,
                            value: selectedStore._id,
                            data: selectedStore
                        } : null}
                        loadOptions={loadStores}
                        onChange={handleStoreSelect}
                        additional={{ page: 1 }}
                        placeholder="Select Branch"
                        menuPortalTarget={document.body}
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                    />

                </div>

                <div className="return-input-group">
                    <label>Customer</label>
                    <AsyncPaginate
                        key={selectedStore?._id || "no-store"}
                        value={selectedCustomer ? {
                            label: `${selectedCustomer.name} (${selectedCustomer.phoneNumber})`,
                            value: selectedCustomer._id,
                            data: selectedCustomer
                        } : null}
                        loadOptions={loadCustomers}
                        onChange={handleCustomerSelect}
                        additional={{ page: 1 }}
                        placeholder="Select Customer"
                        isDisabled={!selectedStore}
                        menuPortalTarget={document.body}
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                    />
                </div>

                <div className="return-input-group">
                    <label>Invoice</label>
                    <AsyncPaginate
                        key={selectedCustomer?._id || "no-customer"}
                        value={selectedInvoice ? {
                            label: selectedInvoice.invoiceNo,
                            value: selectedInvoice._id,
                            data: selectedInvoice
                        } : null}
                        loadOptions={loadInvoices}
                        onChange={handleInvoiceSelect}
                        additional={{ page: 1 }}
                        placeholder="Select Invoice"
                        isDisabled={!selectedCustomer}
                        menuPortalTarget={document.body}
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                    />
                </div>
            </div>

            <div className="return-top">
                <div className="product-box">
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#fff', backgroundColor: '#3C0008' }}>Action</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#fff', backgroundColor: '#3C0008' }}>Product/Service/Membership</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#fff', textAlign: 'center', backgroundColor: '#3C0008' }}>Qty Info</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#fff', textAlign: 'center', backgroundColor: '#3C0008' }}>Return Qty</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#fff', textAlign: 'right', backgroundColor: '#3C0008' }}>Taxable Amt</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#fff', textAlign: 'right', backgroundColor: '#3C0008' }}>GST</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#fff', textAlign: 'right', backgroundColor: '#3C0008' }}>Total Cost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {returnItems.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#666', height: '100px' }}>
                                            No items available for return
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    returnItems.map((item) => (
                                        <TableRow key={item._id} hover>
                                            {/* ACTION FIRST - CHECKBOX */}
                                            <TableCell>
                                                <Checkbox
                                                    checked={item.isSelected || false}
                                                    onChange={() => toggleItemSelection(item._id)}
                                                    size="small"
                                                    sx={{ '& .MuiSvgIcon-root': { fontSize: 18 } }}
                                                />
                                            </TableCell>

                                            {/* PRODUCT / SERVICE NAME */}
                                            <TableCell>{item.name}</TableCell>

                                            {/* PURCHASE QUANTITY */}
                                            <TableCell>
                                                <div style={{ lineHeight: "18px" }}>
                                                    <div>Purchased: <b>{item.originalQty}</b></div>
                                                    <div style={{ color: "orange" }}>Returned: <b>{item.returnedQty}</b></div>
                                                    <div style={{ color: "green" }}>Remaining: <b>{item.remainingQty}</b></div>
                                                </div>
                                            </TableCell>

                                            {/* RETURN QUANTITY WITH NICE UI */}
                                            <TableCell>
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    justifyContent: "center"
                                                }}>
                                                    {/* Decrease Button */}
                                                    <button
                                                        onClick={() => decreaseReturnQty(item._id)}
                                                        disabled={!item.isSelected}
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                            borderRadius: "8px",
                                                            border: "1px solid #9c6769",
                                                            background: item.isSelected ? "#9c6769" : "#e2c8ca",
                                                            color: "white",
                                                            cursor: item.isSelected ? "pointer" : "not-allowed",
                                                            fontSize: "18px",
                                                            fontWeight: "bold",
                                                            transition: "0.2s",
                                                        }}
                                                    >
                                                        –
                                                    </button>

                                                    {/* QUANTITY BOX */}
                                                    <div style={{
                                                        width: "50px",
                                                        height: "32px",
                                                        border: "2px solid #9c6769",
                                                        borderRadius: "6px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        background: "white",
                                                        fontSize: "16px",
                                                        fontWeight: "600",
                                                        color: "#333"
                                                    }}>
                                                        {item.returnQty}
                                                    </div>

                                                    {/* Increase Button */}
                                                    <button
                                                        onClick={() => increaseReturnQty(item._id)}
                                                        disabled={!item.isSelected || item.returnQty >= item.remainingQty}
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                            borderRadius: "8px",
                                                            border: "1px solid #9c6769",
                                                            background: item.isSelected ? "#9c6769" : "#e2c8ca",
                                                            color: "white",
                                                            cursor: item.isSelected ? "pointer" : "not-allowed",
                                                            opacity: item.returnQty >= item.remainingQty ? 0.4 : 1,
                                                            fontSize: "18px",
                                                            fontWeight: "bold",
                                                            transition: "0.2s",
                                                        }}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </TableCell>

                                            {/* PURCHASE COST */}
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatCurrency(calcReturnTaxable(item))}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatCurrency(calcReturnGST(item))}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                                                {formatCurrency(calcReturnRowTotal(item))}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

                <div className="summary-box">
                    <h2>Return Summary</h2>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>Total Products:</span> <span>{totalReturnProducts}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>Total Items:</span> <span>{totalReturnItemsCount}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#666' }}>
                        <span>Taxable Amount:</span> <span>{formatCurrency(selectedTaxableSum)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#666' }}>
                        <span>Total GST:</span> <span>{formatCurrency(selectedGstSum)}</span>
                    </div>
                    {returnDiscount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: 'red' }}>
                            <span>Overall Discount:</span> <span>-{formatCurrency(returnDiscount)}</span>
                        </div>
                    )}
                    <div style={{ borderTop: "1px solid #ddd", marginTop: "8px", paddingTop: "8px", display: 'flex', justifyContent: 'space-between', fontWeight: "bold", fontSize: '1.1rem' }}>
                        <span>Grand Total:</span> <span>{formatCurrency(grandTotal)}</span>
                    </div>
                </div>
            </div>

            <PaymentSection
                paymentList={paymentList}
                setPaymentList={setPaymentList}
                addPaymentRow={addPaymentRow}
                removePaymentRow={removePaymentRow}
                amountRemaining={amountRemaining}
                grandTotal={grandTotal}
            />


            <div className="note-box">
                <label>Note</label>
                <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Type Description" />
                <div className="note-actions">
                    <button className="cancel-Product-button" onClick={() => navigate('/return')}>Cancel</button>
                    <button className="add-Product-button" onClick={handleAddReturn}>Add Return</button>
                </div>
            </div>
        </div >
    );
};

export default ReturnAddPage;

const PaymentSection = ({ paymentList, setPaymentList, addPaymentRow, removePaymentRow, amountRemaining, grandTotal }) => {
    const totalPaid = paymentList.reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0
    );

    return (
        <div className="payment-box" style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "10px"
        }}>
            <h2>Payment</h2>

            {paymentList.map((p, idx) => (
                <div key={idx} className="return-payment-row" style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px"
                }}>
                    <select
                        className="category-input"
                        style={{ flex: 1 }}
                        value={p.method}
                        onChange={(e) => {
                            const updated = [...paymentList];
                            updated[idx] = { ...updated[idx], method: e.target.value };
                            setPaymentList(updated);
                        }}
                    >
                        <option value="SELECT">SELECT</option>
                        <option value="CASH">CASH</option>
                        <option value="UPI">UPI</option>
                        <option value="CARD">CARD</option>
                        <option value="OTHERS">OTHERS</option>
                    </select>

                    <input
                        className="category-input"
                        style={{ flex: 1 }}
                        type="text"
                        value={p.amount}
                        onChange={(e) => {
                            const updated = [...paymentList];
                            updated[idx] = { ...updated[idx], amount: e.target.value };
                            setPaymentList(updated);
                        }}
                        placeholder="Amount"
                    />

                    {paymentList.length > 1 && (
                        <button
                            type="button"
                            className="add-Product-button"
                            style={{ padding: "4px 8px", minWidth: "auto", background: "#ff4d4f" }}
                            onClick={() => removePaymentRow(idx)}
                        >
                            ❌
                        </button>
                    )}
                </div>
            ))}

            {/* <button
                type="button"
                className="add-Product-button"
                onClick={addPaymentRow}
                style={{ marginTop: "8px" }}
            >
                + Add Payment
            </button> */}

            <div style={{ marginTop: "16px", padding: "12px", background: "#f9f9f9", borderRadius: "8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                    <span>Total Paid:</span>
                    <span style={{ fontWeight: "bold" }}>₹{totalPaid.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", color: amountRemaining > 0 ? "red" : "green" }}>
                    <span>Remaining Balance:</span>
                    <span style={{ fontWeight: "bold" }}>₹{amountRemaining.toFixed(2)}</span>
                </div>
            </div>
        </div>
    );
};
